import apiClient from './client'

import type {
  Ausencia,
  AusenciaFilterOptionsResponse,
  AusenciaInput,
  AusenciaPaginatedResponse,
  AusenciaRequestInput,
  AusenciaRequestOptions,
  AusenciaStatus,
  AusenciaType,
  PersonaAvailabilityResponse
} from '@/types/gia'

interface AusenciaFilters {
  page?: number
  centro?: number
  persona?: number
  type?: number
  status?: AusenciaStatus
  fecha_from?: string
  fecha_to?: string
  q?: string
}

const toFormData = (data: Partial<AusenciaInput>) => {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return

    if (value instanceof File) {
      formData.append(key, value)

      return
    }

    formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value))
  })

  return formData
}

const ausenciasApi = {
  list: async (filters: AusenciaFilters = {}): Promise<AusenciaPaginatedResponse> => {
    const response = await apiClient.get<AusenciaPaginatedResponse>('/ausencias/', { params: filters })

    return response.data
  },

  get: async (id: number): Promise<Ausencia> => {
    const response = await apiClient.get<Ausencia>(`/ausencias/${id}/`)

    return response.data
  },

  requestOptions: async (): Promise<AusenciaRequestOptions> => {
    const response = await apiClient.get<AusenciaRequestOptions>('/ausencias/request-options/')

    return response.data
  },

  requestAbsence: async (data: AusenciaRequestInput): Promise<Ausencia> => {
    const response = await apiClient.post<Ausencia>('/ausencias/request/', toFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  },

  create: async (data: AusenciaInput): Promise<Ausencia> => {
    const response = await apiClient.post<Ausencia>('/ausencias/', toFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  },

  update: async (id: number, data: Partial<AusenciaInput>): Promise<Ausencia> => {
    const response = await apiClient.put<Ausencia>(`/ausencias/${id}/`, toFormData(data), {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/ausencias/${id}/`)
  },

  resolve: async (
    id: number,
    data: {
      decision: 'approved' | 'rejected'
      fecha_inicio?: string
      fecha_fin?: string | null
      resolution_message?: string
      override_justificante?: boolean
    }
  ): Promise<Ausencia> => {
    const response = await apiClient.post<Ausencia>(`/ausencias/${id}/resolve/`, data)

    return response.data
  },

  listTypes: async (): Promise<AusenciaType[]> => {
    const response = await apiClient.get<AusenciaType[]>('/ausencias-types/')

    return response.data
  },

  createType: async (data: Partial<AusenciaType>): Promise<AusenciaType> => {
    const response = await apiClient.post<AusenciaType>('/ausencias-types/', data)

    return response.data
  },

  updateType: async (id: number, data: Partial<AusenciaType>): Promise<AusenciaType> => {
    const response = await apiClient.put<AusenciaType>(`/ausencias-types/${id}/`, data)

    return response.data
  },

  deleteType: async (id: number): Promise<void> => {
    await apiClient.delete(`/ausencias-types/${id}/`)
  },

  listFilterOptions: async (filters: Omit<AusenciaFilters, 'page' | 'persona' | 'status' | 'type' | 'q'> = {}): Promise<AusenciaFilterOptionsResponse> => {
    const response = await apiClient.get<AusenciaFilterOptionsResponse>('/ausencias/filter-options/', { params: filters })

    return response.data
  },

  personaAvailability: async (personaId: number): Promise<PersonaAvailabilityResponse> => {
    const response = await apiClient.get<PersonaAvailabilityResponse>('/ausencias/persona-availability/', { params: { persona: personaId } })

    return response.data
  }
}

export default ausenciasApi
